import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getUnreadCount } from '../../services/notificationService';
import { subscribeToNotifications } from '../../services/notificationService';
import { searchAll } from '../../services/searchService';
import { getDefaultAvatar } from '../../lib/defaults';
import {
  HiHome,
  HiUserGroup,
  HiChatBubbleLeftRight,
  HiBell,
  HiMagnifyingGlass,
  HiAcademicCap,
  HiBuildingLibrary,
  HiChevronDown,
  HiArrowRightOnRectangle,
  HiCog6Tooth,
  HiXMark,
  HiSun,
  HiMoon,
  HiBookmark
} from 'react-icons/hi2';
import './Navbar.css';

function Navbar() {
  const { user, profile, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState({ people: [], posts: [], communities: [] });
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [searching, setSearching] = useState(false);
  const [unreadNotifs, setUnreadNotifs] = useState(0);
  const [darkMode, setDarkMode] = useState(() => {
    return localStorage.getItem('theme') === 'dark';
  });
  const searchRef = useRef(null);
  const searchTimerRef = useRef(null);
  const profileMenuRef = useRef(null);

  // Apply theme on mount and change
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', darkMode ? 'dark' : 'light');
    localStorage.setItem('theme', darkMode ? 'dark' : 'light');
  }, [darkMode]);

  useEffect(() => {
    if (user) {
      getUnreadCount(user.id).then(setUnreadNotifs).catch(() => {});
    }
  }, [user, location.pathname]);

  // Realtime notification subscription
  useEffect(() => {
    if (!user) return;
    const unsub = subscribeToNotifications(user.id, () => {
      setUnreadNotifs(prev => prev + 1);
    });
    return () => { if (unsub) unsub(); };
  }, [user]);

  // Close search and profile menu on route change
  useEffect(() => {
    setShowSearchResults(false);
    setSearchQuery('');
    setShowProfileMenu(false);
  }, [location.pathname]);

  // Click outside to close search
  useEffect(() => {
    const handleClick = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowSearchResults(false);
      }
      if (profileMenuRef.current && !profileMenuRef.current.contains(e.target)) {
        setShowProfileMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Debounced search
  const handleSearchChange = useCallback((e) => {
    const val = e.target.value;
    setSearchQuery(val);
    
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    
    if (val.trim().length < 2) {
      setSearchResults({ people: [], posts: [], communities: [] });
      setShowSearchResults(false);
      return;
    }

    setShowSearchResults(true);
    setSearching(true);
    searchTimerRef.current = setTimeout(async () => {
      try {
        const results = await searchAll(val.trim());
        setSearchResults(results || { people: [], posts: [], communities: [] });
      } catch (err) {
        console.error('Search error:', err);
        setSearchResults({ people: [], posts: [], communities: [] });
      } finally {
        setSearching(false);
      }
    }, 300);
  }, []);

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Escape') {
      setShowSearchResults(false);
      e.target.blur();
    }
  };

  const navItems = [
    { path: '/', icon: HiHome, label: 'Home' },
    { path: '/network', icon: HiUserGroup, label: 'My Network' },
    { path: '/communities', icon: HiBuildingLibrary, label: 'Communities' },
    { path: '/messaging', icon: HiChatBubbleLeftRight, label: 'Messaging' },
    { path: '/notifications', icon: HiBell, label: 'Notifications', badge: unreadNotifs || null },
  ];

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  const displayUser = profile || { name: 'User', headline: '', avatar: '' };

  return (
    <nav className="navbar" role="navigation" aria-label="Main navigation">
      <div className="navbar-inner">
        <div className="navbar-left">
          <Link to="/" className="navbar-logo" aria-label="CampusReConnect Home">
            <HiAcademicCap className="logo-icon" />
          </Link>
          <div className="navbar-search" ref={searchRef}>
            <HiMagnifyingGlass className="search-icon" aria-hidden="true" />
            <input
              type="text"
              placeholder="Search researchers, papers, topics..."
              value={searchQuery}
              onChange={handleSearchChange}
              onFocus={() => searchQuery.trim().length >= 2 && setShowSearchResults(true)}
              onKeyDown={handleSearchKeyDown}
              aria-label="Search"
              role="combobox"
              aria-expanded={showSearchResults}
              aria-autocomplete="list"
              aria-controls="search-results-list"
              autoComplete="off"
            />
            {searchQuery && (
              <button
                className="search-clear"
                onClick={() => { setSearchQuery(''); setSearchResults({ people: [], posts: [], communities: [] }); setShowSearchResults(false); }}
                aria-label="Clear search"
              >
                <HiXMark />
              </button>
            )}
            
            {showSearchResults && (
              <div className="search-results-dropdown" role="listbox" id="search-results-list">
                {searching ? (
                  <div className="search-loading">
                    <div className="spinner spinner-sm" />
                    <span>Searching...</span>
                  </div>
                ) : (searchResults.people?.length > 0 || searchResults.posts?.length > 0 || searchResults.communities?.length > 0) ? (
                  <>
                    {/* People Results */}
                    {searchResults.people?.length > 0 && (
                      <>
                        <div className="search-results-header">People</div>
                        {searchResults.people.map(person => (
                          <Link
                            key={person.id}
                            to={`/profile/${person.id}`}
                            className="search-result-item"
                            role="option"
                            onClick={() => setShowSearchResults(false)}
                          >
                            <img
                              src={person.avatar || getDefaultAvatar(person.name)}
                              alt=""
                              className="avatar avatar-sm"
                            />
                            <div className="search-result-info">
                              <span className="search-result-name">{person.name}</span>
                              <span className="search-result-meta">{person.headline?.substring(0, 50)}</span>
                            </div>
                          </Link>
                        ))}
                      </>
                    )}
                    {/* Posts Results */}
                    {searchResults.posts?.length > 0 && (
                      <>
                        <div className="search-results-header">Posts</div>
                        {searchResults.posts.map(post => (
                          <Link
                            key={post.id}
                            to={`/profile/${post.author?.id}`}
                            className="search-result-item"
                            role="option"
                            onClick={() => setShowSearchResults(false)}
                          >
                            <img
                              src={post.author?.avatar || getDefaultAvatar(post.author?.name || 'U')}
                              alt=""
                              className="avatar avatar-sm"
                            />
                            <div className="search-result-info">
                              <span className="search-result-name">{post.author?.name}</span>
                              <span className="search-result-meta">{post.content?.substring(0, 60)}...</span>
                            </div>
                          </Link>
                        ))}
                      </>
                    )}
                    {/* Communities Results */}
                    {searchResults.communities?.length > 0 && (
                      <>
                        <div className="search-results-header">Communities</div>
                        {searchResults.communities.map(community => (
                          <Link
                            key={community.id}
                            to={`/communities/${community.id}`}
                            className="search-result-item"
                            role="option"
                            onClick={() => setShowSearchResults(false)}
                          >
                            <div className="avatar avatar-sm" style={{ background: 'var(--color-primary)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '8px', fontSize: '14px', fontWeight: 700 }}>
                              {community.name?.charAt(0)}
                            </div>
                            <div className="search-result-info">
                              <span className="search-result-name">{community.name}</span>
                              <span className="search-result-meta">{community.description?.substring(0, 50)}</span>
                            </div>
                          </Link>
                        ))}
                      </>
                    )}
                  </>
                ) : (
                  <div className="search-no-results">
                    No results for "{searchQuery}"
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="navbar-center" role="menubar">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`nav-item ${location.pathname === item.path ? 'active' : ''}`}
              role="menuitem"
              aria-label={item.label}
              aria-current={location.pathname === item.path ? 'page' : undefined}
            >
              <div className="nav-item-icon-wrapper">
                <item.icon className="nav-item-icon" />
                {item.badge && <span className="nav-badge" aria-label={`${item.badge} new notifications`}>{item.badge}</span>}
              </div>
              <span className="nav-item-label">{item.label}</span>
            </Link>
          ))}
        </div>

        <div className="navbar-right">
          <button
            className="theme-toggle"
            onClick={() => setDarkMode(!darkMode)}
            aria-label={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            title={darkMode ? 'Light mode' : 'Dark mode'}
          >
            {darkMode ? <HiSun /> : <HiMoon />}
          </button>
          <div ref={profileMenuRef} style={{ position: 'relative' }}>
            <div
              className="profile-trigger"
              onClick={() => setShowProfileMenu(!showProfileMenu)}
              role="button"
              aria-expanded={showProfileMenu}
              aria-haspopup="menu"
              aria-label="Account menu"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && setShowProfileMenu(!showProfileMenu)}
            >
              <img src={displayUser.avatar || getDefaultAvatar(displayUser.name)} alt={displayUser.name} className="nav-avatar" />
              <div className="profile-trigger-text">
                <span className="profile-trigger-name">Me</span>
                <HiChevronDown className="profile-chevron" />
              </div>
            </div>

            {showProfileMenu && (
              <div className="profile-menu" role="menu">
                <div className="profile-menu-header">
                  <img src={displayUser.avatar || getDefaultAvatar(displayUser.name)} alt={displayUser.name} className="avatar" />
                  <div>
                    <h4>{displayUser.name}</h4>
                    <p>{displayUser.headline?.substring(0, 50)}...</p>
                  </div>
                </div>
                <Link
                  to={`/profile/${user?.id}`}
                  className="btn btn-outline btn-sm profile-menu-view"
                  onClick={() => setShowProfileMenu(false)}
                  role="menuitem"
                >
                  View Profile
                </Link>
                <div className="profile-menu-divider" />
                <div className="profile-menu-section">
                  <h5>Account</h5>
                  <Link to={`/profile/${user?.id}`} onClick={() => setShowProfileMenu(false)} role="menuitem">
                    <HiCog6Tooth /> My Profile
                  </Link>
                  <Link to="/saved" onClick={() => setShowProfileMenu(false)} role="menuitem">
                    <HiBookmark /> Saved Posts
                  </Link>
                </div>
                <div className="profile-menu-divider" />
                <button className="profile-menu-logout" onClick={handleLogout} role="menuitem">
                  <HiArrowRightOnRectangle /> Sign Out
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}

export default Navbar;
